import React, { useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { graphql, useStaticQuery } from 'gatsby';
import classNames from 'classnames/bind';

import Heading from 'components/shared/heading';
import Button from 'components/shared/button';
import Link from 'components/shared/link';

import CloseIcon from 'icons/close-icon.inline.svg';
import shape from './images/shape.svg';
import dots from './images/dots-pattren.svg';

import styles from './popup-banner.module.scss';

const cx = classNames.bind(styles);

const KEY_LOCAL_STORAGE = 'popup-banner';

const PopupBanner = ({ location, buttonText, isCookieBannerVisible, setIsPopupBannerVisible }) => {
  const [isReady, setIsReady] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const [defaultState, setDefaultState] = useState({
    id: '',
    title: '',
    description: '',
    path: '',
  });

  const {
    allWpPdfResource: { nodes: items },
  } = useStaticQuery(
    graphql`
      query {
        allWpPdfResource(sort: { fields: date, order: ASC }) {
          nodes {
            id
            title
            uri
            acf {
              previewDescription
              popupBanner {
                ... on WpPost {
                  uri
                }
                ... on WpPage {
                  uri
                }
              }
            }
          }
        }
      }
    `
  );

  // save data in local storage for further processing of the display of window
  const getLocalStorage = () => JSON.parse(localStorage.getItem(KEY_LOCAL_STORAGE));
  const setLocalStorage = (id) => {
    const identifiers = getLocalStorage() || { [id]: true };

    if (!(id in identifiers)) {
      identifiers[id] = true;
    }

    localStorage.setItem(KEY_LOCAL_STORAGE, JSON.stringify(identifiers));
  };

  const handleOpen = useCallback(() => {
    const { outerHeight: heightPage, scrollY } = window;
    // calc: show window when the page is scrolled more than 40%
    const scrollPercentage = (heightPage / 100) * 40;

    if (scrollY >= scrollPercentage) {
      setIsPopupBannerVisible(true);
      return setIsOpen(true);
    }
    return setIsOpen(false);
  }, []);

  const handleButtonClose = useCallback(() => {
    setIsOpen(false);
    setLocalStorage(defaultState.id);
    setIsPopupBannerVisible(false);
    window.removeEventListener('scroll', handleOpen);
  }, [defaultState.id, handleOpen]);

  const setState = () => {
    let { pathname } = location;
    // mandatory addition of slash to the link
    // Possible discrepancy: banner uri: /video-intercom-for-apartments/ === pathname: /video-intercom-for-apartments - not equal
    // Сorrects: banner uri: /video-intercom-for-apartments/ === pathname: /video-intercom-for-apartments/ - equal
    pathname = pathname[pathname.length - 1] === '/' ? pathname : `${pathname}/`;

    items.forEach(({ id, title, uri: path, acf: { popupBanner, previewDescription } }) => {
      if (!popupBanner) {
        return;
      }

      popupBanner.map(({ uri }) => {
        if (pathname === uri) {
          setDefaultState({
            id,
            title,
            description: previewDescription,
            path,
          });
          return setIsReady(true);
        }
        return null;
      });
    });
  };

  useEffect(() => {
    if (!isReady) {
      setState();
    } else {
      // if there is no data on this pdf resource in the local storage, then open
      if (!getLocalStorage() || !getLocalStorage()[defaultState.id]) {
        window.addEventListener('scroll', handleOpen);

        return () => {
          window.removeEventListener('scroll', handleOpen);
        };
      }
      return null;
    }

    return null;
  }, [isReady, handleOpen, defaultState.id]);

  return (
    <div className={cx('wrapper', { isOpen: isReady && isOpen }, { isCookieBannerVisible })}>
      <div className={cx('content')}>
        <Link className={cx('title-wrapper')} to={defaultState.path}>
          <Heading className={cx('title')} size="md" tag="h5">
            {defaultState.title}
          </Heading>
        </Link>
        {defaultState.description && (
          <p
            className={cx('description')}
            dangerouslySetInnerHTML={{ __html: defaultState.description }}
          />
        )}
      </div>
      <Button className={cx('button')} to={defaultState.path} size="sm" theme="accent-primary">
        {buttonText}
      </Button>

      <button className={cx('close')} type="button" onClick={handleButtonClose}>
        <CloseIcon />
      </button>

      <img className={cx('shape')} src={shape} alt="" loading="lazy" aria-hidden />
      <img className={cx('dots')} src={dots} alt="" loading="lazy" aria-hidden />
    </div>
  );
};

PopupBanner.propTypes = {
  location: PropTypes.objectOf(PropTypes.any).isRequired,
  buttonText: PropTypes.string,
  isCookieBannerVisible: PropTypes.bool.isRequired,
  setIsPopupBannerVisible: PropTypes.func.isRequired,
};

PopupBanner.defaultProps = {
  buttonText: 'Download PDF',
};

export default PopupBanner;
